// src/pages/Home2.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import ResidentSidebar from '../components/ResidentSidebar';
import '../css/Home.css';
import '../css/Home2.css';
import LogoPolderzicht from '../assets/logo_polderzicht_kantoorlaag.png';
import EditableWidget from '../components/EditableWidget';
import { 
  getAnnouncements, 
  saveAnnouncements, 
  getCalendarEvents, 
  saveCalendarEvents 
} from '../components/widgetDataService';
import EnergyComparisonWidget from '../components/EnergyComparisonWidget';
import EnergyCostsWidget from '../components/EnergyCostsWidget';

const Home2 = () => {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'nl');
  const [announcements, setAnnouncements] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const navigate = useNavigate();
  const isAuthenticated = !!localStorage.getItem('token');

  // Load saved widget data on component mount
  useEffect(() => {
    const loadWidgetData = async () => {
      try {
        // Load announcements
        const loadedAnnouncements = await getAnnouncements();
        setAnnouncements(loadedAnnouncements);
        
        // Load calendar events
        const loadedEvents = await getCalendarEvents();
        setCalendarEvents(loadedEvents);
      } catch (error) {
        console.error('Error loading widget data:', error);
      }
    };
    
    loadWidgetData();
  }, []);
  
  // Save announcements to API/backend
  const handleSaveAnnouncements = async (newAnnouncements) => {
    try {
      const success = await saveAnnouncements(newAnnouncements);
      if (success) {
        setAnnouncements(newAnnouncements);
        alert('Announcements saved successfully!');
      } else {
        alert('Error saving announcements. Changes are temporarily saved locally.');
      }
    } catch (error) {
      console.error('Error saving announcements:', error);
      alert('Error saving announcements. Please try again.');
    }
  };
  
  // Save calendar events to API/backend
  const handleSaveCalendarEvents = async (newEvents) => {
    try {
      const success = await saveCalendarEvents(newEvents);
      if (success) {
        setCalendarEvents(newEvents);
        alert('Calendar events saved successfully!');
      } else {
        alert('Error saving calendar events. Changes are temporarily saved locally.');
      }
    } catch (error) {
      console.error('Error saving calendar events:', error);
      alert('Error saving calendar events. Please try again.');
    }
  };
  
  // Create translations
  const translations = {
    nl: {
      home: "Home",
      newResidents: "Nieuwe bewoners",
      regulations: "Huishoudelijk Reglement",
      faq: "Veelgestelde vragen", 
      contact: "Contact",
      calendar: "Kalender",
      admin: "Beheer",
      login: "Inloggen",
      logout: "Uitloggen",
      welcome: "Welkom bij VVE Polderzicht",
      subtitle: "Op deze website vind je handige documenten, informatie en contactgegevens",
      infoForNewResidents: "Informatie voor nieuwe bewoners",
      infoDescription: "Welkom in Polderzicht! Hier vindt u alle informatie die u nodig heeft als nieuwe bewoner.",
      moreInfo: "Meer informatie",
      regulations: "Huishoudelijk Reglement",
      regulationsDescription: "Lees het huishoudelijk reglement en de splitsingsakte van VVE Polderzicht.",
      viewDocuments: "Bekijk documenten",
      faqTitle: "Veelgestelde vragen",
      faqDescription: "Antwoorden op veelgestelde vragen over wonen in Polderzicht.",
      viewFAQ: "Bekijk FAQ",
      announcements: "Laatste mededelingen",
      contactTitle: "Contact",
      contactDescription: "Heeft u vragen of opmerkingen? Neem contact op met het bestuur.",
      manager: "Beheerder",
      phone: "Telefoon",
      calendarTitle: "Kalender",
      calendarDescription: "Bekijk geplande evenementen en onderhoudswerkzaamheden.",
      viewFullCalendar: "Bekijk volledige agenda",
      meeting: "Algemene Ledenvergadering",
      elevator: "Onderhoud lift",
      cleaning: "Schoonmaak gemeenschappelijke ruimtes",
      energyUsage: "Energieverbruik",
      energyDescription: "Bekijk het energieverbruik van het gebouw en vergelijk met voorgaande jaren."
    },
    en: {
      home: "Home",
      newResidents: "New Residents",
      regulations: "House Rules",
      faq: "FAQ", 
      contact: "Contact",
      calendar: "Calendar",
      admin: "Admin",
      login: "Login",
      logout: "Logout",
      welcome: "Welcome to VVE Polderzicht",
      subtitle: "On this website you'll find useful documents, information and contact details",
      infoForNewResidents: "Information for new residents",
      infoDescription: "Welcome to Polderzicht! Here you'll find all the information you need as a new resident.",
      moreInfo: "More information",
      regulations: "House Rules",
      regulationsDescription: "Read the house rules and the deed of division of VVE Polderzicht.",
      viewDocuments: "View documents",
      faqTitle: "Frequently Asked Questions",
      faqDescription: "Answers to frequently asked questions about living in Polderzicht.",
      viewFAQ: "View FAQ",
      announcements: "Latest announcements",
      contactTitle: "Contact",
      contactDescription: "Do you have questions or comments? Contact the board.",
      manager: "Manager",
      phone: "Phone",
      calendarTitle: "Calendar",
      calendarDescription: "View scheduled events and maintenance work.",
      viewFullCalendar: "View full calendar",
      meeting: "General Members Meeting",
      elevator: "Elevator maintenance",
      cleaning: "Cleaning of common areas",
      energyUsage: "Energy Usage",
      energyDescription: "View the building's energy consumption and compare with previous years."
    }
  };
  
  // Get current texts based on selected language
  const t = translations[language];

  // Function to handle external links
  const openExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="page-with-sidebar">
      {/* Import ResidentSidebar component */}
      <ResidentSidebar language={language} setLanguage={setLanguage} />
      
      {/* Main content */}
      <main className="home-main-content">
        <div className="home-header">
          <img src={LogoPolderzicht} alt="Polderzicht Logo" className="logo-image" />
          <h1>{t.welcome}</h1>
          <p>{t.subtitle}</p>
        </div>

        {/* Masonry layout for widgets */}
        <Masonry
          breakpointCols={{
            default: 3,  // 3 columns on desktop
            1200: 2,     // 2 columns on smaller desktop/tablets
            768: 1       // 1 column on mobile
          }}
          className="my-masonry-grid"
          columnClassName="my-masonry-grid_column"
        >
          {/* Widget: New Residents Information */}
          <div className="home-widget">
            <h3>{t.infoForNewResidents}</h3>
            <p>{t.infoDescription}</p>
            <button onClick={() => navigate('/infonieuw')} className="widget-button">
              {t.moreInfo}
            </button>
          </div>

          {/* Widget: Regulations */}
          <div className="home-widget">
            <h3>{t.regulations}</h3>
            <p>{t.regulationsDescription}</p>
            <button onClick={() => navigate('/hhr')} className="widget-button">
              {t.viewDocuments}
            </button>
          </div>

          {/* Widget: FAQ */}
          <div className="home-widget">
            <h3>{t.faqTitle}</h3>
            <p>{t.faqDescription}</p>
            <button onClick={() => navigate('/faq')} className="widget-button">
              {t.viewFAQ}
            </button>
          </div>

          {/* Widget: Contact Information */}
          <div className="home-widget">
            <h3>{t.contactTitle}</h3>
            <p>{t.contactDescription}</p>
            <p><strong>E-mail:</strong> <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a></p>
            <p><strong>{t.manager}:</strong> Van Roemburg VVE Beheer</p>
            <p><strong>{t.phone}:</strong> 085-7600225</p>
          </div>

          {/* Widget: Energy Comparison (existing - two columns wide) */}
          <div className="energy-widget-container">
            <EnergyComparisonWidget language={language} />
          </div>

     

        </Masonry>

             {/* Widget: Energy Costs Explanation - Home widget version */}
             <EnergyCostsWidget language={language} />

        <footer className="home-footer">
          <p>&copy; {new Date().getFullYear()} VVE Polderzicht • <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a></p>
          <div className="footer-language-switcher">
            <button 
              className={`footer-lang-btn ${language === 'nl' ? 'active' : ''}`} 
              onClick={() => setLanguage('nl')}
            >
              NL
            </button>
            <span className="lang-separator">|</span>
            <button 
              className={`footer-lang-btn ${language === 'en' ? 'active' : ''}`} 
              onClick={() => setLanguage('en')}
            >
              EN
            </button>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Home2;